import { SSOUserRole } from '@models/CommunityMember'
import GatedFeatures from '@utils/gated-features'
import { isLearnPcPreview } from '@utils/page-routes'

import type Community from '@models/Community'
import type CommunityMember from '@models/CommunityMember'
import type {
  ClientSettings,
  Group,
  SessionType,
  SessionUser,
  UserGroups,
  VisitedScore,
} from '@stores/globalData/server-side'

export class Session {
  current_user: SessionUser | null
  community: Community
  community_member: CommunityMember | null
  locale: string
  user_groups: UserGroups
  showSellerFeatures: boolean
  visited_scores: VisitedScore[]
  gatedFeatures: GatedFeatures
  clientSettings: ClientSettings

  private readonly _communityGroup?: Group

  constructor(session: SessionType, client_settings: ClientSettings) {
    this.current_user = session.current_user
    this.community = session.community
    this.community_member = session.community_member
    this.locale = session.locale
    this.user_groups = session.user_groups
    this.showSellerFeatures = session.showSellerFeatures
    this.visited_scores = session.visited_scores ?? []
    this.gatedFeatures = new GatedFeatures(this)
    this.clientSettings = client_settings ?? {}

    this._communityGroup = this.user_groups?.groups?.find((group) => group.is_community_group)
  }

  get plan(): string {
    if (this.community.default_community) {
      if (this.current_user) {
        if (this.current_user.premium_member) return 'premium'
        return 'free'
      }
      return 'anonymous'
    }
    return 'learn'
  }

  /** Whether the user is currently signed in */
  isLoggedIn(): boolean {
    return !!this.current_user
  }

  /** Whether the user is currently in the default community */
  isDefaultCommunity(): boolean {
    return this.community.default_community
  }

  /**
   * Similar to {@link isDefaultCommunity} except that it also excludes the Learn Premium Content
   * Preview. This should be used when needing to check for behavior that only the public community
   * will use.
   */
  isNonLearnCommunity(pathname?: string): boolean {
    return this.isDefaultCommunity() && !isLearnPcPreview(pathname)
  }

  /** Whether or not the user is in a configured site */
  isSite(): boolean {
    return !this.isDefaultCommunity()
  }

  isAnonymousNonDefaultCommunity(): boolean {
    return !this.isLoggedIn() && !this.isDefaultCommunity()
  }

  isAnonymousNonPublicCommunity(): boolean {
    return !this.isLoggedIn() && !this.isDefaultCommunity() && !this.community.allow_anonymous_view
  }

  // region Roles
  isNoteflightAdmin(): boolean {
    return this.isSiteAdmin() && this.isDefaultCommunity()
  }

  isSiteAdminOrTeacher(): boolean {
    return this.isSiteAdmin() || this.isSiteTeacher()
  }

  isSiteAdmin(): boolean {
    return !!this.current_user?.effective_admin
  }

  isSiteTeacher(): boolean {
    return this.isLoggedIn() && this.community_member?.sso_user_role === SSOUserRole.INSTRUCTOR
  }

  isSiteStudent(): boolean {
    return this.isLoggedIn() && this.community_member?.sso_user_role === SSOUserRole.STUDENT
  }
  // endregion Roles

  // region Helpers
  hasSoundCheck(): boolean {
    return !!this.communityMember?.has_sound_check
  }

  hasPremiumFeatures(): boolean {
    return !!this.current_user?.has_premium_features
  }
  // endregion Helpers

  get currentUser(): SessionUser | null {
    return this.current_user
  }

  get communityMember(): CommunityMember | null {
    return this.community_member
  }

  get communityGroup(): Group | null {
    return this._communityGroup ?? null
  }

  updateProfile({ first_name, last_name }) {
    if (this.current_user) {
      this.current_user.profile = {
        ...this.current_user.profile,
        first_name,
        last_name,
      }
    }
  }
}

export default Session
